// FLAGS: identity, authorization, personal-data
// Google code-flow login, 2FA enrolment and verification, and the role predicates every UI gate in the dashboard asks.
import { AuthenticatorTwoFactorCodeRequestMethod } from "@src/constants";
import { User } from "@src/models/User";
import { OpenUpRoles } from "@src/services/OpenUpRoles";
import {
  OpenUpHttpClient,
  OpenUpHttpClientV2,
} from "@src/api/OpenUpHttpClient";
import { ExternalLogin } from "@src/api/v2/ExternalLogin";
import { GoogleCalendar } from "@src/api/v1/GoogleCalendar";
import {
  ModelsRequestSource,
  ResponseExternalLoginRegisterCodeFlowResponse,
  ResponseLoginCodeFlowResponse,
  ResponseUserResponse,
} from "@src/api/v2/data-contracts";
import { Users as UsersV2 } from "@src/api/v2/Users";
import { Users as UsersV1 } from "@src/api/v1/Users";
import {
  clearAuthenticatedUserContext,
  setAuthenticatedUserContext,
} from "@src/utils/appInsights";

// FLAGS: identity, personal-data
// Writes the whole profile response to `sessionStorage` under "user" and pins the operator's id into App Insights.
// The credential itself stays in the cookie, but the roles the UI trusts now live somewhere the page can rewrite.
export function setUserToStorage(
  user: ResponseUserResponse | ResponseExternalLoginRegisterCodeFlowResponse,
) {
  sessionStorage.setItem("user", JSON.stringify(user));
  if ("id" in user && user.id) {
    setAuthenticatedUserContext(user.id);
  }
}

export function clearUserFromStorage() {
  sessionStorage.removeItem("user");
  clearAuthenticatedUserContext();
}

const isTwoFactorPendingResponse = (
  response: ResponseExternalLoginRegisterCodeFlowResponse,
) =>
  !!response.twoFactorVerification &&
  response.twoFactorVerification === "pending";

export const getUserProfile = () =>
  new UsersV2(OpenUpHttpClientV2).getUserProfileV2().then((response) => {
    setUserToStorage(response);
    return response;
  });

const grantCalendarAccess = (code: string) => {
  let { hostname: domain } = globalThis.location;
  if (!!globalThis.location.port && globalThis.location.port !== "443") {
    domain = `${domain}:${globalThis.location.port}`;
  }

  return new GoogleCalendar(OpenUpHttpClient).grantAccess({
    code,
    domain,
  });
};

const registerExternalUser = async (
  code: string,
  provider: string,
): Promise<
  ResponseUserResponse | ResponseExternalLoginRegisterCodeFlowResponse
> => {
  let { hostname: domain } = globalThis.location;
  if (!!globalThis.location.port && globalThis.location.port !== "443") {
    domain = `${domain}:${globalThis.location.port}`;
  }
  return new ExternalLogin(OpenUpHttpClientV2)
    .registerCodeFlowV2({
      code,
      provider,
      domain,
      requestSource: ModelsRequestSource.IsDashboard,
    })
    .then(async (response) => {
      if (isTwoFactorPendingResponse(response)) {
        setUserToStorage(response);
        return response;
      }
      return getUserProfile();
    })
    .catch((error) => {
      throw error;
    });
};

const loginExternalUser = async (
  code: string,
  provider: string,
): Promise<ResponseUserResponse | ResponseLoginCodeFlowResponse> => {
  let domain = globalThis.location.hostname;
  if (!!globalThis.location.port && globalThis.location.port !== "443") {
    domain = `${domain}:${globalThis.location.port}`;
  }
  return new ExternalLogin(OpenUpHttpClientV2)
    .loginCodeFlowV2({
      code,
      provider,
      domain,
      requestSource: ModelsRequestSource.IsDashboard,
    })
    .then(async (response) => {
      if (isTwoFactorPendingResponse(response)) {
        setUserToStorage(response);
        return response;
      }
      return getUserProfile();
    })
    .catch((error) => {
      throw error;
    });
};

const getTwoFactorRegistration = async () => {
  return new UsersV1(OpenUpHttpClient).registerTwoFactorLogin().catch(() => {});
};

const setTwoFactorLogin = async () => {
  return new UsersV1(OpenUpHttpClient)
    .enableTwoFactorLogin()
    .then((response) => {
      sessionStorage.setItem("user", JSON.stringify(response));
      return response;
    });
};

const authenticateTwoFactorCode = async (
  value: string,
  method = AuthenticatorTwoFactorCodeRequestMethod,
) => {
  return new UsersV1(OpenUpHttpClient)
    .validateToken({ code: value, method })
    .then(() => getUserProfile());
};

export const getCurrentUser = () => {
  const user = sessionStorage.getItem("user");
  return (user ? JSON.parse(user) : null) as User | null;
};

// FLAGS: authorization
// Reads roles out of `sessionStorage`, so this decides what the operator *sees*, never what they may do. It also
// returns false on any parse problem, which renders as "no permission" rather than as an error.
const hasRole = (...roles: OpenUpRoles[]) => {
  const userRoles = getCurrentUser()?.roles;

  try {
    if (userRoles) {
      if (Array.isArray(userRoles)) {
        return roles.some((role) => userRoles.includes(role));
      }
      return roles.includes(userRoles);
    }
    return false;
  } catch {
    return false;
  }
};

const getUserRoles = () =>
  getCurrentUser()?.roles.map((role) => OpenUpRoles[role]);
const isExpert = () => hasRole(OpenUpRoles.Expert);
const isManagerOrAdmin = () => hasRole(OpenUpRoles.Manager, OpenUpRoles.Admin);
const isHost = () => hasRole(OpenUpRoles.Host);
const isManagerOrAdminOrHost = () =>
  hasRole(OpenUpRoles.Manager, OpenUpRoles.Admin, OpenUpRoles.Host);
const isManagerOrAdminOrSupport = () =>
  hasRole(OpenUpRoles.Manager, OpenUpRoles.Admin, OpenUpRoles.Support);
const isManagerOrAdminOrHostOrSupport = () =>
  hasRole(
    OpenUpRoles.Manager,
    OpenUpRoles.Admin,
    OpenUpRoles.Host,
    OpenUpRoles.Support,
  );
const isSupport = () => hasRole(OpenUpRoles.Support);

// FLAGS: identity
// Reads a "refresh_token" key that nothing in this app ever writes, so it always returns null. Refresh is cookie-borne
// via the interceptor; a caller that starts trusting this to mean "not logged in" would be reading a constant.
export const getRefreshToken = () => sessionStorage.getItem("refresh_token");

// checking if user has set up google 2FA authenticator
const twoFactorEnabled = () =>
  !!getCurrentUser()?.userTokenProviders.find(
    (tokenProvider) => tokenProvider === "AuthenticatorKey",
  );

const service = {
  twoFactorEnabled,
  getTwoFactorRegistration,
  authenticateTwoFactorCode,
  setTwoFactorLogin,
  hasRole,
  getUserRoles,
  grantCalendarAccess,
  registerExternalUser,
  loginExternalUser,
  isHost,
  isExpert,
  isManagerOrAdmin,
  isManagerOrAdminOrHost,
  isManagerOrAdminOrSupport,
  isManagerOrAdminOrHostOrSupport,
  isSupport,
};

export default service;
